import { useState } from "react";
import { CheckCircle2, XCircle, Upload, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Section, Field, Empty } from "../common";
import { api, errMsg, useFetch } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { fdate, fdt } from "../../lib/format";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.eml,.msg,.docx";

export default function AcceptanceTab({ q, reload }) {
  const { t } = useT();
  const { can } = useAuth();
  const { data: rows, reload: reloadRows } = useFetch(`/quotations/${q.id}/acceptances`);
  const [decision, setDecision] = useState("accepted");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const canRecord = can("quote:accept") && q.status === "sent" && q.current_rev != null && !q.dirty;
  const submit = async () => {
    if (!file) return toast.error(t("acc.fileRequired"));
    const fd = new FormData();
    fd.append("decision", decision); fd.append("rev", String(q.current_rev)); fd.append("note", note); fd.append("received_at", date); fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/quotations/${q.id}/acceptance`, fd);
      toast.success(t(`status.${decision}`)); reloadRows(); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const download = async (a) => {
    try {
      const r = await api.get(`/quotations/${q.id}/acceptances/${a.id}/evidence`, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const el = document.createElement("a"); el.href = href; el.download = a.filename; document.body.appendChild(el); el.click(); el.remove();
      setTimeout(() => URL.revokeObjectURL(href), 5000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reason = q.current_rev == null ? t("acc.needPublish") : q.dirty ? t("acc.needRepublish") : q.status !== "sent" ? t("acc.alreadyDecided") : t("ws.readonly");
  return (
    <div className="grid gap-4 xl:grid-cols-[1fr_1.3fr] [&>*]:min-w-0">
      <Section title={t("acc.record")} testid="section-acceptance-form">
        {!canRecord ? <p className="text-sm text-neutral-600" data-testid="acceptance-unavailable">{reason}</p> : (
          <div className="space-y-4">
            <div className="text-xs text-neutral-600">{t("acc.revision")}: <b className="rounded-sm bg-black px-1.5 py-0.5 font-mono text-white">Rev {q.current_rev}</b></div>
            <div className="grid grid-cols-2 gap-2">
              {[["accepted", CheckCircle2, "border-emerald-500 bg-emerald-50 text-emerald-800"], ["rejected", XCircle, "border-red-500 bg-red-50 text-red-800"]].map(([k, Icon, on]) => (
                <button key={k} data-testid={`acceptance-decision-${k}`} onClick={() => setDecision(k)}
                  className={`flex items-center justify-center gap-2 rounded border-2 px-3 py-3 text-sm font-bold transition-colors ${decision === k ? on : "border-neutral-200 text-neutral-500 hover:border-neutral-400"}`}>
                  <Icon className="h-5 w-5" />{t(`status.${k}`)}
                </button>
              ))}
            </div>
            <Field label={t("acc.receivedAt")} required><Input data-testid="acceptance-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
            <Field label={t("acc.evidence")} required>
              <Input data-testid="acceptance-file-input" type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <p className="text-[11px] text-neutral-500">{t("acc.fileHint")}</p>
            </Field>
            <Field label={t("common.notes")}><Textarea data-testid="acceptance-note-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
            <Button data-testid="acceptance-submit-btn" disabled={busy} onClick={submit} className="w-full"><Upload className="h-4 w-4" />{busy ? "…" : t("acc.submit")}</Button>
          </div>
        )}
      </Section>
      <Section title={t("acc.history")} testid="section-acceptance-history">
        <div className="overflow-x-auto">
          <table className="dense-table w-full" data-testid="acceptance-table">
            <thead><tr><th>{t("common.date")}</th><th>Rev</th><th>{t("acc.decision")}</th><th>{t("common.notes")}</th><th>{t("ws.by")}</th><th>{t("acc.evidence")}</th></tr></thead>
            <tbody>
              {(rows || []).map((a) => (
                <tr key={a.id} data-testid={`acceptance-row-${a.id}`}>
                  <td className="whitespace-nowrap">{fdate(a.received_at)}<div className="text-[10px] text-neutral-400">{fdt(a.at)}</div></td>
                  <td>Rev {a.rev}</td>
                  <td><span className={`rounded-sm border px-1.5 py-0.5 text-[11px] font-semibold ${a.decision === "accepted" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`}>{t(`status.${a.decision}`)}</span></td>
                  <td className="max-w-[200px] truncate">{a.note}</td>
                  <td>{a.recorded_by_name}</td>
                  <td><Button size="sm" variant="ghost" data-testid={`acceptance-download-${a.id}`} onClick={() => download(a)}><FileDown className="h-4 w-4" />{a.filename}</Button>
                    <div className="font-mono text-[10px] text-neutral-400" title={a.sha256}>{a.sha256?.slice(0, 12)}…</div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows && !rows.length && <Empty>{t("common.empty")}</Empty>}
        </div>
      </Section>
    </div>
  );
}
